import type { EmailIntegrationConfiguration } from "../../integrations/definitions/EmailIntegration";
import EmailIntegration from "../../integrations/definitions/EmailIntegration";
import type { IntegrationDto, TriggerDto } from "../../services/ApiService";
import { integrationsByType } from "../../integrations/integrations";
import IntegrationId from "../../integrations/IntegrationId";
import type { Integration, IntegrationDefinition, IntegrationType } from "../../integrations/types";
import type { FieldConfig, VariableConfig } from "../../fields/types";

// From dto to domain
export const triggerToIntegration = (
  trigger: TriggerDto,
  fields: FieldConfig[],
  variables: VariableConfig[],
): Integration => ({
  id: IntegrationId.random(),
  type: "mail",
  legacy: EmailIntegration.legacy,
  name: trigger.name,
  configuration: EmailIntegration.fromDto(trigger, fields, variables),
});

export const toIntegration = (
  integrationDto: IntegrationDto,
  fields: FieldConfig[],
  variables: VariableConfig[],
): Integration => {
  const definition = integrationsByType[integrationDto.key as IntegrationType];

  return {
    id: IntegrationId.from(integrationDto.uid),
    type: definition.type,
    legacy: definition.legacy,
    name: integrationDto.name,
    configuration: definition.fromDto(
      integrationDto.configuration as typeof definition extends IntegrationDefinition<unknown, infer B> ? B : never,
      fields,
      variables,
    ),
  };
};

// From domain to dto
const toTriggerDto = (
  integration: Integration<EmailIntegrationConfiguration>,
  fields: FieldConfig[],
  variables: VariableConfig[],
): TriggerDto => ({
  type: integration.type,
  name: integration.name,
  ...EmailIntegration.toDto(integration.configuration, fields, variables),
});

const toIntegrationDto = <T>(
  integration: Integration<T>,
  fields: FieldConfig[],
  variables: VariableConfig[],
): IntegrationDto => {
  const definition = integrationsByType[integration.type] as IntegrationDefinition<T, Record<string, unknown>>;

  return {
    uid: integration.id.toString(),
    name: integration.name,
    configuration: definition.toDto(integration.configuration, fields, variables),
    ...integration.legacy,
  };
};

const isMailIntegration = (integration: Integration): integration is Integration<EmailIntegrationConfiguration> =>
  integration.type === "mail";

export const toTriggerDtos = (integrations: Integration[], fields: FieldConfig[], variables: VariableConfig[]) =>
  integrations.filter(isMailIntegration).map((integration) => toTriggerDto(integration, fields, variables));

export const toIntegrationDtos = (integrations: Integration[], fields: FieldConfig[], variables: VariableConfig[]) =>
  integrations
    .filter((integration) => !isMailIntegration(integration))
    .map((integration) => toIntegrationDto(integration, fields, variables));
